import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/client'
import { TreePicker } from '@/components/tree-picker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { TREE_API, treeKey } from '@/features/trees/api'
import { formatAmount } from '@/lib/quantity'

import { MOVEMENT_LABELS, applyMovement, type MovementKind } from './api'

export interface MovementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: MovementKind
  lot: {
    id: string
    amount: string
    amount_unknown: boolean
    location_id: string | null
  }
  unitSymbol?: string | null
  /** Whether this part is counted in whole units. */
  wholeUnitsOnly: boolean
  onDone: () => Promise<void> | void
  /** Where a move goes, when that is already decided (a drawer it was dropped on). */
  initialTarget?: string | null
  /** The amount to start from, e.g. the whole lot for a drag. */
  initialAmount?: string
}

export function MovementDialog({
  open,
  onOpenChange,
  kind,
  lot,
  unitSymbol,
  wholeUnitsOnly,
  onDone,
  initialTarget = null,
  initialAmount = '',
}: MovementDialogProps) {
  const [amount, setAmount] = useState(initialAmount)
  const [comment, setComment] = useState('')
  const [target, setTarget] = useState<string | null>(initialTarget)
  const [error, setError] = useState<string | null>(null)

  const locations = useQuery({
    queryKey: treeKey('locations'),
    queryFn: () => TREE_API.locations.tree(),
    enabled: kind === 'move',
  })

  const submit = useMutation({
    mutationFn: () =>
      applyMovement(lot.id, {
        kind,
        amount: amount.trim(),
        target_location_id: kind === 'move' ? target : undefined,
        comment: comment.trim(),
      }),
    onSuccess: async () => {
      onOpenChange(false)
      reset()
      toast.success('Stock updated')
      await onDone()
    },
    onError: (caught) => {
      // A refused movement is information, not a failure: it says how much is
      // actually there, or why the location will not take it.
      if (caught instanceof ApiError) {
        setError(caught.problem.errors?.[0]?.message ?? caught.message)
        return
      }
      setError(caught instanceof Error ? caught.message : 'Could not update the stock')
    },
  })

  const reset = () => {
    setAmount(initialAmount)
    setComment('')
    setTarget(initialTarget)
    setError(null)
  }

  const disabled =
    submit.isPending ||
    amount.trim() === '' ||
    (kind === 'move' && target === null) ||
    (wholeUnitsOnly && !Number.isInteger(Number(amount)))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{MOVEMENT_LABELS[kind]}</DialogTitle>
          <DialogDescription>
            {lot.amount_unknown
              ? 'This lot has never been counted.'
              : `There ${formatAmount(lot.amount) === '1' ? 'is' : 'are'} ${formatAmount(
                  lot.amount,
                  unitSymbol,
                )} in this lot.`}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            submit.mutate()
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="movement-amount">
              {kind === 'set' ? 'Counted amount' : 'Amount'}
              {unitSymbol ? ` (${unitSymbol})` : ''}
            </Label>
            <Input
              id="movement-amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode={wholeUnitsOnly ? 'numeric' : 'decimal'}
              autoFocus
              aria-invalid={error ? true : undefined}
            />
            {wholeUnitsOnly && (
              <p className="text-muted-foreground text-xs">Counted in whole units.</p>
            )}
          </div>

          {kind === 'move' && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="movement-target">Move to</Label>
              <TreePicker
                id="movement-target"
                label="Move to"
                nodes={locations.data ?? []}
                value={target}
                placeholder="Choose a location"
                onChange={setTarget}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="movement-comment">Note</Label>
            <Input
              id="movement-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={kind === 'set' ? 'counted them' : 'optional'}
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={disabled}>
              {submit.isPending && <Spinner />}
              {MOVEMENT_LABELS[kind]}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
